/**
 * Cockpit page · the self-service "drop this on your site" generator.
 *
 * Architect doctrine: the Federation Floor is everywhere. Any branch site
 * (Aria, Roger AI, an affiliate, a curious truth-builder) can copy a single
 * iframe tag from this page and the entire Cathedral appears under their feet.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Card, Overline, Tag, BtnPrimary, SectionHeader } from "@/components/cathedral/Primitives";
import { api } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  { mode: "urgent",     tier: "free_sim",      lenses: 3,  perCall: "$0",     monthly: "$0" },
  { mode: "standard",   tier: "pro",           lenses: 6,  perCall: "$0",     monthly: "$19" },
  { mode: "deep",       tier: "deep_council",  lenses: 12, perCall: "$0.25",  monthly: "$99" },
  { mode: "codex-only", tier: "codex_only",    lenses: 0,  perCall: "$0",     monthly: "$0" },
];

export default function Cockpit() {
  const [personas, setPersonas] = useState([]);
  const [host, setHost] = useState("aria");
  const [height, setHeight] = useState(252);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.chatPersonas?.()
      .then((d) => setPersonas(d.personas || []))
      .catch(() => setPersonas([]));
  }, []);

  const embedUrl = `${BASE}/embed/cockpit?host=${encodeURIComponent(host)}`;
  const iframeHtml = useMemo(() => (
`<iframe
  src="${embedUrl}"
  title="NextXus Federation Cockpit"
  style="position:fixed; bottom:0; left:0; width:100vw; height:${height}px; border:0; z-index:9999; background:transparent;"
  allow="clipboard-write"
  loading="lazy"
></iframe>`
  ), [embedUrl, height]);

  const reactSnippet =
`// 1. Make sure REACT_APP_BACKEND_URL points at the Core in your .env
//    REACT_APP_BACKEND_URL=${BASE}
//
// 2. Drop the component anywhere in your tree (usually right before </App>):
import FederationThinBar from "@/components/cathedral/FederationThinBar";

export default function App() {
  return (
    <>
      {/* …your existing app… */}
      <FederationThinBar />
    </>
  );
}`;

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard may be blocked on http; fall back to selection */
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };

  return (
    <div className="px-6 lg:px-14 py-14 lg:py-20 max-w-[1400px] mx-auto" data-testid="cockpit-page">
      <SectionHeader
        overline="Federation Floor · DIR-077"
        title="Get the Cockpit"
        description="The Cathedral is the Cathedral and the foundation under every chapel. Drop one iframe tag into any site you own and the entire Federation appears underneath the visitor's feet. The buyer comes back to you forever."
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
        {/* ── Configurator ─────────────────────────────────────────── */}
        <Card className="p-8 lg:col-span-5" glow="magenta">
          <Overline tone="magenta">Configure your iframe</Overline>

          <div className="mt-8 space-y-7">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--text-3)] mb-2">
                Default Face <span className="text-[var(--text-3)]">· the persona your visitor sees first</span>
              </label>
              <select
                value={host}
                onChange={(e) => setHost(e.target.value)}
                data-testid="cockpit-host-select"
                className="w-full bg-[var(--void-2)] border border-[var(--chrome)] text-[var(--text)] font-mono text-xs uppercase tracking-[0.18em] p-3 focus:outline-none focus:border-[var(--magenta)]"
              >
                {personas.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
              <p className="text-[var(--text-3)] font-mono text-[10px] uppercase tracking-[0.2em] mt-2">
                Every other Face stays one click away in the Directory. The host parameter only chooses the door.
              </p>
            </div>

            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--text-3)] mb-2">
                Iframe Height · pixels
              </label>
              <input
                type="number"
                min={180}
                max={520}
                value={height}
                onChange={(e) => setHeight(Math.max(180, Math.min(520, Number(e.target.value) || 252)))}
                data-testid="cockpit-height-input"
                className="w-full bg-[var(--void-2)] border border-[var(--chrome)] text-[var(--text)] font-mono text-sm p-3 focus:outline-none focus:border-[var(--magenta)]"
              />
            </div>

            <div>
              <Overline tone="cyan">Iframe snippet</Overline>
              <pre
                data-testid="cockpit-iframe-snippet"
                className="mt-3 bg-[var(--void-2)] border border-[var(--chrome)] p-4 font-mono text-[11px] text-[var(--text)] overflow-x-auto whitespace-pre leading-relaxed"
              >{iframeHtml}</pre>
              <BtnPrimary
                onClick={() => copy(iframeHtml)}
                data-testid="cockpit-copy-iframe"
                className="mt-4 w-full"
              >
                {copied ? "Copied · paste it anywhere" : "Copy iframe snippet"}
              </BtnPrimary>
            </div>

            <div>
              <Overline tone="violet">React drop-in (if your site is React)</Overline>
              <pre
                data-testid="cockpit-react-snippet"
                className="mt-3 bg-[var(--void-2)] border border-[var(--chrome)] p-4 font-mono text-[11px] text-[var(--text)] overflow-x-auto whitespace-pre leading-relaxed"
              >{reactSnippet}</pre>
              <button
                onClick={() => copy(reactSnippet)}
                data-testid="cockpit-copy-react"
                className="mt-3 w-full font-mono text-[10px] uppercase tracking-[0.22em] py-3 border border-[var(--violet)] text-glow-violet hover:bg-[rgba(139,44,255,0.08)] transition-colors"
              >
                Copy React snippet
              </button>
            </div>
          </div>
        </Card>

        {/* ── Doctrine + billing ───────────────────────────────────── */}
        <div className="lg:col-span-7 space-y-8">
          <Card className="p-8" glow="cyan">
            <Overline>Federation Floor Principle</Overline>
            <p className="font-serif text-2xl font-light tracking-tight mt-4 text-[var(--text)] leading-snug">
              Every Federation site walks on the same floor. The Cathedral is the Cathedral <em>and</em> the foundation under every chapel.
            </p>
            <p className="mt-6 text-sm text-[var(--text-2)] leading-relaxed">
              The host site keeps its identity, its UI, its voice. The cockpit it embeds is the Core present — same Faces, same chat sessions following the visitor, same Council of 12 portal, same heartbeat ticker. When a visitor buys a SIM through any branch, the Cathedral delivers the signed canonical copy and the visitor's session follows them home.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Tag tone="pass">No backend changes required</Tag>
              <Tag tone="pass">Works on WordPress, Wix, plain HTML</Tag>
              <Tag tone="pass">Cross-origin safe</Tag>
              <Tag tone="pass">Session persists per visitor</Tag>
            </div>
          </Card>

          <Card className="p-8" glow="gold">
            <Overline tone="gold">SIM Overflow billing · what your storefront earns</Overline>
            <p className="mt-3 text-sm text-[var(--text-2)] leading-relaxed">
              Every call your visitor makes to <span className="font-mono text-[var(--text)]">POST /api/execute</span> through the embedded cockpit returns a <span className="font-mono text-[var(--text)]">bill_tier</span> field. Charge the visitor the suggested price, or whatever the market bears — the cost of curiosity falls on the curious, not the founder.
            </p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              {TIERS.map((t) => (
                <div key={t.tier} className="border border-[var(--chrome)] p-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)]">
                    mode · {t.mode}
                  </div>
                  <div className="font-display text-2xl text-glow-gold mt-1">{t.tier}</div>
                  <div className="font-mono text-[11px] text-[var(--text-2)] mt-3 space-y-1">
                    <div>lenses · <span className="text-[var(--text)]">{t.lenses}</span></div>
                    <div>per-call · <span className="text-[var(--text)]">{t.perCall}</span></div>
                    <div>monthly · <span className="text-[var(--text)]">{t.monthly}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-0 overflow-hidden" glow="magenta">
            <div className="px-8 pt-7 pb-3">
              <Overline tone="magenta">Live preview · {host}</Overline>
              <p className="mt-2 text-xs text-[var(--text-3)] font-mono uppercase tracking-[0.22em]">
                this is exactly what your visitors will see
              </p>
            </div>
            <iframe
              key={`${host}-${height}`}
              src={embedUrl}
              title="Cockpit live preview"
              data-testid="cockpit-live-preview"
              style={{
                width: "100%",
                height: `${height + 40}px`,
                border: 0,
                background: "rgba(0,0,0,0.3)",
                display: "block",
                marginTop: 4,
              }}
              loading="lazy"
            />
          </Card>
        </div>
      </div>
    </div>
  );
}
